import {
  Button,
  Table,
  type TableProps,
} from '@omnipresentgroup/design-system';
import type { MergeIntegrationEmployee } from '@omnipresentgroup/types';
import {
  type ColumnDef,
  type ColumnOrderState,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';

import {
  MergeEmployeesColumnHeaders,
  useMergeEmployeesTableConfig,
} from '../hooks';

const ERROR_STATE_TITLE = "Uh-oh, something's gone wrong";
const ERROR_STATE_DESCRIPTION =
  'Try reloading the page, and if this fails get in touch with our support team.';

type Props = {
  columnVisibility: Partial<{
    [key in keyof typeof MergeEmployeesColumnHeaders]: boolean;
  }>;
  columnOrderOverride?: (keyof typeof MergeEmployeesColumnHeaders)[];
  optionsOverride?: Omit<
    TableOptions<MergeIntegrationEmployee>,
    'data' | 'columns'
  >;
  columnsOverride?: ColumnDef<MergeIntegrationEmployee, unknown>[];
  usePagination?: boolean;
  emptyStateProps?: TableProps['emptyStateProps'];
  useSorting?: boolean;
  employees: MergeIntegrationEmployee[];
};

export const MergeEmployeeList = ({
  columnVisibility,
  columnOrderOverride,
  optionsOverride,
  columnsOverride,
  emptyStateProps,
  employees,
  usePagination = true,
}: Props) => {
  const [columnVisibilityState, setColumnVisibilityState] = useState<
    Props['columnVisibility']
  >({
    // Default all columns to false visibility
    ...Object.fromEntries(
      Object.keys(MergeEmployeesColumnHeaders).map((current) => [
        current,
        false,
      ]),
    ),
    // Enable columns from consumer
    ...columnVisibility,
  });

  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(
    columnOrderOverride || [],
  );

  const { columns } = useMergeEmployeesTableConfig({
    columnsOverride,
  });

  const tableOptions: Omit<
    TableOptions<MergeIntegrationEmployee>,
    'data' | 'columns'
  > = {
    state: {
      columnVisibility: columnVisibilityState,
      columnOrder,
    },
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibilityState,
    onColumnOrderChange: setColumnOrder,
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    ...optionsOverride,
  };

  const table = useReactTable({
    data: employees,
    columns: columns,
    ...tableOptions,
  });

  return (
    <Table
      id="merge-employee-list"
      table={table}
      showHeader={false}
      errorStateProps={{
        id: 'merge-employees-list-error',
        title: ERROR_STATE_TITLE,
        description: ERROR_STATE_DESCRIPTION,
        callToAction: <Button onClick={window.location.reload}>Retry</Button>,
      }}
      emptyStateProps={emptyStateProps}
      useSorting
      usePagination={usePagination}
    />
  );
};
